import { createContext, useContext, useEffect, useState } from 'react';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenExpiringSoon, setTokenExpiringSoon] = useState(false);

  // Función para renovar token automáticamente
  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No hay refresh token');
      }

      const response = await fetch(buildApiUrl(`${API_ENDPOINTS.AUTH}/refresh-token`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const { token, refreshToken: newRefreshToken, user } = await response.json();
        localStorage.setItem('authToken', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setTokenExpiringSoon(false);
        return token;
      } else {
        throw new Error('Error al renovar token');
      }
    } catch (error) {
      console.error('Error renovando token:', error);
      logout();
      throw error;
    }
  };

  // Función para hacer peticiones autenticadas con renovación automática
  const authenticatedFetch = async (url, options = {}) => {
    let token = localStorage.getItem('authToken');
    
    console.log('🔐 authenticatedFetch:', { 
      url, 
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'No token'
    });
    
    if (!token) {
      throw new Error('No hay token de autenticación');
    }

    // Intentar la petición con el token actual
    let response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });

    // Si el token expiró, intentar renovarlo
    if (response.status === 401) {
      try {
        token = await refreshToken();
        // Reintentar la petición con el nuevo token
        response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        throw new Error('Error de autenticación', error);
      }
    }

    // Verificar si el token está próximo a expirar
    const expiringSoon = response.headers.get('X-Token-Expiring-Soon');
    if (expiringSoon === 'true') {
      setTokenExpiringSoon(true);
    }

    return response;
  };

  useEffect(() => {
    // Verificar si hay un usuario autenticado al cargar la app
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // Verificar si el token está próximo a expirar
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = tokenData.exp - now;
        const thirtyMinutes = 30 * 60; // 30 minutos en segundos
        
        if (timeUntilExpiry <= thirtyMinutes) {
          setTokenExpiringSoon(true);
        }
              } catch {
          // Si hay error al parsear, limpiar datos corruptos
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
    }
    
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(buildApiUrl(`${API_ENDPOINTS.AUTH}/login`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const { token, refreshToken, user } = await response.json();
        localStorage.setItem('authToken', token);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setTokenExpiringSoon(false);
        return { success: true, user };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.message || 'Error de autenticación' };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, error: 'Error de conexión al servidor' };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        // Intentar hacer logout en el servidor
        await fetch(buildApiUrl(`${API_ENDPOINTS.AUTH}/logout`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      // Limpiar datos locales independientemente del resultado del servidor
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      setTokenExpiringSoon(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.AUTH}/profile/${user.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const { user: updatedUser } = await response.json();
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.message || 'Error al actualizar perfil' };
      }
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      return { success: false, error: 'Error de conexión al servidor' };
    }
  };

  const value = {
    user,
    login,
    logout,
    updateProfile,
    authenticatedFetch,
    refreshToken,
    loading,
    isAuthenticated: !!user,
    tokenExpiringSoon,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 